'use client';

import { useEffect, useRef, useState } from 'react';

import Link from 'next/link';

import OxxoChart from '@/components/ui/Content/OxxoChart';

import { motion, useAnimation } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      staggerChildren: 0.15
    }
  }
};

const childVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3 }
  }
};

const chartVariants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4 }
  }
};

export default function ChartsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          controls.start('visible');
        }
      },
      { threshold: 0.2 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [controls]);

  return (
    <section className='bg-background px-6 py-16 md:py-24'>
      <motion.div
        ref={sectionRef}
        className='mx-auto flex w-full max-w-[1400px] flex-col items-center'
        initial='hidden'
        animate={controls}
        variants={containerVariants}>
        {/* Header */}
        <motion.div
          className='mb-12 text-center md:mb-16'
          variants={childVariants}>
          <p className='text-secondary mb-3 text-[10px] font-black tracking-[0.35em] uppercase'>
            Visual intelligence
          </p>
          <h2
            className='text-foreground text-3xl leading-tight font-black tracking-tight md:text-5xl'
            style={{ color: '#066653' }}>
            Check out some of our charts!
          </h2>
          <p className='text-muted-foreground mt-4 text-base md:text-lg'>
            We turn noisy data into clear, beautiful visual stories.
          </p>
        </motion.div>

        {/* Main Chart Graphic with 3D skill motion container */}
        <motion.div
          className='bg-card/30 w-full rounded-[2rem] border border-[#066653] p-4 sm:p-6 md:rounded-[2.5rem] md:p-8 dark:border-[#a3f7bf]'
          variants={chartVariants}
          whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}>
          <OxxoChart />
        </motion.div>

        {/* Footer CTA with custom amazing subscribe button motion */}
        <motion.div
          className='mt-12 text-center md:mt-16'
          variants={childVariants}>
          <Link href='/articles'>
            <motion.button
              className='bg-background hover:bg-secondary/10 dark:border-secondary dark:text-secondary dark:hover:bg-secondary/20 inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#066653] px-8 py-4 text-[13px] font-black tracking-widest text-[#066653] uppercase transition-colors duration-300'
              whileHover={{
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.95 }}>
              Go to Articles
              <ArrowRight className='size-4' />
            </motion.button>
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}
